import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Shield,
  TrendingUp,
  Lock,
  ExternalLink,
  Loader2,
  XCircle,
  AlertCircle,
} from 'lucide-react';

const ExchangeConnection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [formData, setFormData] = useState({
    api_key: '',
    api_secret: '',
  });

  useEffect(() => {
    checkExistingConnection();
  }, []);

  const checkExistingConnection = async () => {
    try {
      const response = await api.getExchangeStatus();
      if (response.data.connected) {
        setConnected(true);
      }
    } catch (error) {
      console.error('Failed to check connection status');
    }
  };

  const getErrorMessage = (error) => {
    const detail = error.response?.data?.detail || '';
    
    if (detail.includes('Invalid') || detail.includes('key')) {
      return {
        type: 'invalid_key',
        message: 'Invalid API Key format. Please copy the key exactly as shown in Kraken.',
        suggestions: [
          'Ensure you copied the entire API key without extra spaces',
          'Verify the key was created for the correct Kraken account',
          'Check if the API key has been revoked in Kraken'
        ]
      };
    }
    
    if (detail.includes('Signature') || detail.includes('authentication') || detail.includes('secret')) {
      return {
        type: 'invalid_secret',
        message: 'Invalid API Secret. The private key may be incorrect.',
        suggestions: [
          'API secrets are only shown once during creation',
          'If you lost your secret, create a new API key in Kraken',
          'Ensure you copied the secret immediately after creation'
        ]
      };
    }
    
    if (detail.includes('permission') || detail.includes('Enable')) {
      return {
        type: 'permissions',
        message: 'API key permissions incorrect.',
        suggestions: [
          'Ensure "Create & Modify Orders" permission is enabled',
          'Do NOT enable "Withdraw Funds" permission',
          'Recreate the API key with correct permissions if needed'
        ]
      };
    }
    
    return {
      type: 'unknown',
      message: 'Connection failed. Please verify your API credentials.',
      suggestions: [
        'Double-check both API key and private key',
        'Ensure trading permissions are enabled on Kraken',
        'Try creating a new API key if the issue persists'
      ]
    };
  };

  const handleTestConnection = async () => {
    if (!formData.api_key || !formData.api_secret) {
      toast.error('Please enter both API key and private key');
      return;
    }

    setTesting(true);
    setConnectionError(null);
    
    try {
      const response = await api.connectExchange({
        api_key: formData.api_key.trim(),
        api_secret: formData.api_secret.trim(),
        exchange: 'kraken',
      });
      if (response.data?.note) {
        toast.success(response.data.note);
      } else {
        toast.success('Kraken account connected successfully!');
      }
      setConnected(true);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      setConnectionError(errorInfo);
      toast.error(errorInfo.message);
    } finally {
      setTesting(false);
    }
  };

  if (connected) {
    return (
      <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
        <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
          <div className="container mx-auto px-4 md:px-8 h-16 flex items-center">
            <Button
              data-testid="exchange-back-btn"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="text-[#94A3B8] hover:text-[#F5F7FA]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Button>
          </div>
        </header>

        <div className="container mx-auto px-4 md:px-8 py-16">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-500" />
              </div>
            </div>
            <h1 data-testid="exchange-connected-title" className="text-4xl font-semibold font-['Outfit'] mb-4">Kraken Account Connected!</h1>
            <p className="text-[#94A3B8] text-lg mb-8">
              Your Kraken account is now connected. You can start creating trading bots with paper trading mode.
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                data-testid="exchange-success-strategies-btn"
                onClick={() => navigate('/strategies')}
                className="bg-[#2F6BFF] hover:bg-[#2F6BFF]/90 rounded-sm"
                size="lg"
              >
                Choose Strategies
              </Button>
              <Button
                data-testid="exchange-success-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="border-[#1E293B] rounded-sm"
                size="lg"
              >
                Go to Dashboard
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center">
          <Button
            data-testid="exchange-back-btn"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-[#94A3B8] hover:text-[#F5F7FA]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8">
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
          <h1 className="text-4xl font-semibold font-['Outfit'] mb-2">Connect Kraken Account</h1>
          <p className="text-[#94A3B8] mb-8">
            Link your Kraken account to enable automated trading. Paper trading only — no withdrawals allowed.
          </p>
        </motion.div>

        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}>
          <Alert className="mb-8 bg-[#2F6BFF]/10 border-[#2F6BFF]/50">
            <Shield className="h-5 w-5 text-[#2F6BFF]" />
            <AlertDescription className="text-[#F5F7FA]">
              <strong>Important:</strong> To start automated trading, you need to connect your Kraken account. Apexion
              will only have trading permissions — <strong>no withdrawal access</strong>.
            </AlertDescription>
          </Alert>
        </motion.div>

        {connectionError && (
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
            <Alert className="mb-8 bg-[#EF4444]/10 border-[#EF4444]/50">
              <XCircle className="h-5 w-5 text-[#EF4444]" />
              <AlertDescription>
                <div className="text-[#F5F7FA]">
                  <p className="font-semibold mb-2">{connectionError.message}</p>
                  <p className="text-sm text-[#94A3B8] mb-2">Common solutions:</p>
                  <ul className="text-sm space-y-1">
                    {connectionError.suggestions.map((suggestion, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-[#EF4444] mt-0.5 flex-shrink-0" />
                        <span>{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AlertDescription>
            </Alert>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
            <Card className="bg-[#15171C] border-[#1E293B] h-full">
              <CardHeader>
                <CardTitle className="font-['Outfit'] flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#2F6BFF]" />
                  Step 1: Create Kraken API Keys
                </CardTitle>
                <CardDescription className="text-[#94A3B8]">Follow these steps carefully</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {[
                    { num: '1', text: 'Log in to your Kraken account' },
                    { num: '2', text: 'Go to Settings → API → Create New Key' },
                    { num: '3', text: 'Name it "Apexion Trading"' },
                    { num: '4', text: 'Enable "Create & Modify Orders" and "Query Open Orders & Trades"' },
                    { num: '5', text: 'DO NOT enable "Withdraw Funds"', critical: true },
                    { num: '6', text: 'Copy your API Key and Private Key immediately' },
                  ].map((step, idx) => (
                    <div
                      key={idx}
                      className={`flex items-start gap-3 p-3 rounded-sm ${
                        step.critical ? 'bg-[#EF4444]/10 border border-[#EF4444]/50' : 'bg-[#0B0D12]'
                      }`}
                    >
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          step.critical ? 'bg-[#EF4444] text-white' : 'bg-[#2F6BFF] text-white'
                        }`}
                      >
                        {step.num}
                      </div>
                      <p className={`text-sm ${step.critical ? 'text-[#EF4444] font-semibold' : 'text-[#F5F7FA]'}`}>
                        {step.text}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="pt-4">
                  <a
                    href="https://www.kraken.com/u/security/api"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-[#2F6BFF] hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Kraken API Management
                  </a>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
            <Card className="bg-[#15171C] border-[#1E293B] h-full">
              <CardHeader>
                <CardTitle className="font-['Outfit'] flex items-center gap-2">
                  <Lock className="w-5 h-5 text-[#2F6BFF]" />
                  Step 2: Enter API Credentials
                </CardTitle>
                <CardDescription className="text-[#94A3B8]">Your keys are encrypted and secure</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="api_key" className="text-[#F5F7FA] mb-2 block">API Key</Label>
                  <Input
                    data-testid="exchange-api-key-input"
                    id="api_key"
                    type="text"
                    placeholder="Enter your Kraken API Key"
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    className="bg-[#0B0D12] border-[#1E293B] text-[#F5F7FA] rounded-sm font-['JetBrains_Mono']"
                  />
                  <p className="text-xs text-[#94A3B8] mt-1">
                    Copy the complete API key from Kraken without spaces
                  </p>
                </div>

                <div>
                  <Label htmlFor="api_secret" className="text-[#F5F7FA] mb-2 block">Private Key</Label>
                  <Input
                    data-testid="exchange-api-secret-input"
                    id="api_secret"
                    type="password"
                    placeholder="Enter your Kraken Private Key"
                    value={formData.api_secret}
                    onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                    className="bg-[#0B0D12] border-[#1E293B] text-[#F5F7FA] rounded-sm font-['JetBrains_Mono']"
                  />
                  <p className="text-xs text-[#94A3B8] mt-1">
                    Private key is only shown once during API key creation
                  </p>
                </div>

                <div className="p-4 bg-[#0B0D12] border border-[#1E293B] rounded-sm">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-[#2F6BFF] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-[#F5F7FA] font-semibold mb-1">Your keys are secure</p>
                      <p className="text-xs text-[#94A3B8]">
                        API keys are encrypted before storage. Apexion can only execute trades — never withdraw funds.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  data-testid="exchange-test-connection-btn"
                  onClick={handleTestConnection}
                  disabled={testing || !formData.api_key || !formData.api_secret}
                  className="w-full bg-[#2F6BFF] hover:bg-[#2F6BFF]/90 rounded-sm"
                  size="lg"
                >
                  {testing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Testing Connection...
                    </>
                  ) : (
                    'Test Connection'
                  )}
                </Button>

                <div className="text-center">
                  <p className="text-sm text-[#94A3B8]">
                    Status: <span className="text-[#EF4444] font-semibold">Not Connected</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8"
        >
          <Alert className="bg-[#EF4444]/10 border-[#EF4444]/50">
            <AlertTriangle className="h-5 w-5 text-[#EF4444]" />
            <AlertDescription className="text-[#F5F7FA]">
              <strong>Critical:</strong> Never enable withdrawal permissions on your Kraken API key. Apexion only needs
              trading access. Double-check your API settings before connecting.
            </AlertDescription>
          </Alert>
        </motion.div>
      </div>
    </div>
  );
};

export default ExchangeConnection;
